import React from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

/** Roles stored in the shared admin_users table. */
export type AdminShellRole = 'admin' | 'superadmin' | 'editor';

export interface AdminNavItem<T extends string = string> {
  id: T;
  label: string;
  icon?: React.ElementType;
  /** Shown only when greater than zero. */
  badge?: number;
}

/**
 * Class strings taken from the Hub admin, so other apps can match its
 * controls without copying layout markup.
 */
export const adminUi = {
  page: 'max-w-6xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12 space-y-6',
  input:
    'px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500',
  primaryButton:
    'px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-lg text-sm disabled:opacity-50',
  secondaryButton:
    'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm disabled:opacity-50',
  tabActive: 'bg-teal-600/20 text-teal-300 border border-teal-500/30',
  tabIdle: 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent',
  card: 'bg-slate-900 border border-slate-800 rounded-2xl',
} as const;

export interface AdminShellProps<T extends string = string> {
  /** Platform, or the app name currently in scope. */
  scope: string;
  role: AdminShellRole;
  onBack: () => void;
  backLabel?: string;
  tabs: AdminNavItem<T>[];
  activeTab: T;
  onTabChange: (id: T) => void;
  /** Section heading under the tab bar. Omit on Hub, which has none. */
  title?: string;
  description?: string;
  actions?: React.ReactNode;
  /** embedded matches Hub's in-page admin. overlay is the full-screen app admin. */
  variant?: 'embedded' | 'overlay';
  children: React.ReactNode;
}

export function AdminShell<T extends string>({
  scope,
  role,
  onBack,
  backLabel = 'Dashboard',
  tabs,
  activeTab,
  onTabChange,
  title,
  description,
  actions,
  variant = 'embedded',
  children,
}: AdminShellProps<T>) {
  const body = (
    <div className={adminUi.page}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400 rounded"
        >
          <ArrowLeft size={16} />
          {backLabel}
        </button>
        <div className="flex-1 min-w-[1rem]" />
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck size={20} className="text-teal-400" />
          <h1 className="text-xl font-bold text-white">ReHub Admin</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
            {scope}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-teal-900/30 text-teal-400 border border-teal-800">
            {role}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-800 pb-3" role="tablist" aria-label={`${scope} admin`}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400 ${
                selected ? adminUi.tabActive : adminUi.tabIdle
              }`}
            >
              {Icon ? <Icon size={14} /> : null}
              {tab.label}
              {tab.badge != null && tab.badge > 0 ? (
                <span className="ml-0.5 min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold inline-flex items-center justify-center">
                  {tab.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {(title || description || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title ? <h2 className="text-lg font-semibold text-white">{title}</h2> : null}
            {description ? <p className="text-sm text-slate-400 mt-0.5">{description}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
        </div>
      )}

      {children}
    </div>
  );

  if (variant === 'overlay') {
    return (
      <div className="fixed inset-0 z-[99999] bg-black overflow-y-auto">
        {body}
      </div>
    );
  }

  return body;
}
